/**
 * Auth0 authentication hook for PKCE flow.
 *
 * This hook provides Auth0 integration with PKCE (Proof Key for Code Exchange)
 * which is the recommended flow for SPAs.
 *
 * Usage:
 * - Import { useAuth0, Auth0Provider } from this file
 * - Wrap your app with Auth0Provider
 * - Use useAuth0() hook in components to access auth state and methods
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from '@/hooks/use-toast'
import api from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { authApi } from '@/services/authApi'

// Auth0 configuration - loaded from backend
interface Auth0Config {
  domain: string
  clientId: string
  audience: string
}

// User info from Auth0 token and backend
interface Auth0User {
  auth0Id: string
  email: string
  emailVerified: boolean
  name: string
  picture: string
  permissions: string[]
  role?: string
  // Additional fields from backend
  id?: string
  firstName?: string
  lastName?: string
}

interface Auth0ContextType {
  user: Auth0User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: Error | null
  loginWithRedirect: () => void
  logout: (returnTo?: string) => void
  getAccessToken: () => string | null
  refreshUser: () => Promise<void>
}

const Auth0Context = createContext<Auth0ContextType | undefined>(undefined)

interface Auth0ProviderProps {
  children: React.ReactNode
}

// PKCE helper functions
function generateRandomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  const randomValues = new Uint8Array(length)
  crypto.getRandomValues(randomValues)
  return Array.from(randomValues)
    .map(v => charset[v % charset.length])
    .join('')
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

// Decode JWT token payload (without verification - verification happens on backend)
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

// Error response from auth0/login endpoint
interface Auth0LoginError {
  success: false
  error_code: string
  message: string
  support_email?: string
}

export function Auth0Provider({ children }: Auth0ProviderProps) {
  const isE2EBypass = import.meta.env.VITE_DISABLE_AUTH === 'true'

  const [user, setUser] = useState<Auth0User | null>(null)
  const [isLoading, setIsLoading] = useState(!isE2EBypass)
  const [error, setError] = useState<Error | null>(null)
  const [config, setConfig] = useState<Auth0Config | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const callbackProcessingRef = useRef(false) // Prevent double callback execution

  // Load Auth0 configuration from backend
  useEffect(() => {
    if (isE2EBypass) return
    // Don't run Auth0 logic if we're in an iframe (Auth0 MFA page loads our app in iframe)
    if (window.self !== window.top) {
      setIsLoading(false)
      return
    }

    async function loadConfig() {
      try {
        const response = await api.get('/auth/config')
        setConfig(response.data)
      } catch (err: unknown) {
        const axiosError = err as { response?: { status: number } }
        // Only treat as "not configured" if we get 404 or empty config
        // For other errors (401 expired token, 500, network), the config might still be valid
        // 404 means Auth0 not configured; other errors are transient
        setIsLoading(false)
      }
    }
    loadConfig()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isE2EBypass is constant (env var)
  }, [])

  // Refresh user info from backend
  const refreshUser = useCallback(async () => {
    if (isE2EBypass) return
    const token = localStorage.getItem('auth0_access_token')
    if (!token) {
      setUser(null)
      setIsLoading(false)
      return
    }

    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await api.get('/auth/me')
      const userData = response.data

      // Fetch permissions from /auth/permissions (Consumer Duty pattern)
      let permissions: string[] = []
      let permRoles: string[] = []
      try {
        const permData = await authApi.getPermissions()
        permissions = permData.permissions || []
        permRoles = permData.roles || []
      } catch (permErr) {
      // Error handled silently
      }

      setUser({
        auth0Id: userData.auth0_id,
        email: userData.email,
        emailVerified: userData.email_verified,
        name: userData.name,
        picture: userData.picture,
        permissions,
        role: userData.role || (permRoles.length > 0 ? permRoles[0] : undefined),
      })

      // Sync to Zustand authStore so Sidebar and useAuth() have permissions
      useAuthStore.setState({
        user: {
          id: userData.id,
          email: userData.email,
          first_name: userData.first_name,
          last_name: userData.last_name,
          is_active: userData.is_active,
          is_verified: userData.is_verified,
          created_at: userData.created_at,
          role: userData.role || (permRoles.length > 0 ? permRoles[0] : undefined),
          permissions,
          is_super_admin: userData.is_super_admin,
          subscription_active: userData.subscription_active,
          subscription_tier: userData.subscription_tier,
          has_app_access: userData.has_app_access,
          is_client_admin: userData.is_client_admin,
          is_superuser_for_app: userData.is_superuser_for_app,
        },
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (err: unknown) {

      // Check if this is a "user not found in database" error
      const axiosError = err as { response?: { status: number; data?: { detail?: { error_code?: string; message?: string } } } }

      if (axiosError.response?.status === 401) {
        const errorDetail = axiosError.response.data?.detail
        if (errorDetail?.error_code === 'USER_NOT_FOUND' || errorDetail?.error_code === 'USER_INACTIVE') {
          // User has valid Auth0 token but no account in this app
          // Store error info and redirect to account-not-found
          // DO NOT clear Auth0 tokens here - user may have access to other apps
          const email = localStorage.getItem('auth0_user_email') || ''
          sessionStorage.setItem('auth0_login_error', JSON.stringify({
            email,
            errorCode: errorDetail.error_code,
            message: errorDetail.message || 'No account found for this email.',
          }))

          // Clear local app tokens but keep Auth0 session
          localStorage.removeItem('auth0_access_token')
          localStorage.removeItem('auth0_id_token')
          delete api.defaults.headers.common['Authorization']
          setUser(null)

          // Redirect to account-not-found page
          navigate('/account-not-found')
          return
        }
      }

      // For other errors, clear tokens
      localStorage.removeItem('auth0_access_token')
      localStorage.removeItem('auth0_id_token')
      delete api.defaults.headers.common['Authorization']
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isE2EBypass is constant (env var)
  }, [navigate])

  // Handle callback from Auth0
  useEffect(() => {
    if (isE2EBypass) return
    async function handleCallback() {
      if (location.pathname !== '/callback') return

      // Prevent double execution
      if (callbackProcessingRef.current) {
        return
      }
      callbackProcessingRef.current = true

      const params = new URLSearchParams(location.search)
      const code = params.get('code')
      const state = params.get('state')
      const errorParam = params.get('error')

      if (errorParam) {
        const errorDescription = params.get('error_description') || 'Authentication failed'
        setError(new Error(errorDescription))
        toast({ title: errorDescription, variant: 'destructive' })
        callbackProcessingRef.current = false
        navigate('/login')
        return
      }

      if (!code) {
        callbackProcessingRef.current = false
        return
      }

      // Verify state to prevent CSRF
      const savedState = sessionStorage.getItem('auth0_state')
      if (state !== savedState) {
        setError(new Error('Invalid state parameter'))
        toast({ title: 'Authentication failed: invalid state', variant: 'destructive' })
        callbackProcessingRef.current = false
        navigate('/login')
        return
      }

      // Exchange code for tokens
      const codeVerifier = sessionStorage.getItem('auth0_code_verifier')
      if (!codeVerifier) {
        setError(new Error('Missing code verifier'))
        callbackProcessingRef.current = false
        navigate('/login')
        return
      }

      try {
        // Step 1: Exchange code for tokens
        const tokenResponse = await api.post('/auth/callback', {
          code,
          redirect_uri: `${window.location.origin}/callback`,
          code_verifier: codeVerifier,
        })

        const { access_token, id_token } = tokenResponse.data

        // Store tokens
        localStorage.setItem('auth0_access_token', access_token)
        if (id_token) {
          localStorage.setItem('auth0_id_token', id_token)
        }

        // Clean up PKCE state
        sessionStorage.removeItem('auth0_state')
        sessionStorage.removeItem('auth0_code_verifier')

        // Set token in API client
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

        // Step 2: Decode token to get user info for auth0/login call
        const tokenPayload = decodeJwtPayload(id_token || access_token)
        if (!tokenPayload) {
          throw new Error('Failed to decode token')
        }

        const auth0Id = tokenPayload.sub as string
        const email = tokenPayload.email as string
        const name = tokenPayload.name as string || ''
        const emailVerified = tokenPayload.email_verified as boolean || false
        const picture = tokenPayload.picture as string || ''

        // Store email for potential error messages (used if /auth/me fails later)
        localStorage.setItem('auth0_user_email', email)

        // Step 3: Call backend to match user with local database
        try {
          const loginResponse = await api.post('/auth/auth0/login', {
            auth0_id: auth0Id,
            email,
            name,
            email_verified: emailVerified,
            picture,
          })

          const userData = loginResponse.data.user

          // Fetch permissions from /auth/permissions (Consumer Duty pattern)
          let loginPermissions: string[] = []
          let loginPermRoles: string[] = []
          try {
            const permData = await authApi.getPermissions()
            loginPermissions = permData.permissions || []
            loginPermRoles = permData.roles || []
          } catch (permErr) {
          // Error handled silently
          }

          // Set user with combined Auth0 and backend data
          setUser({
            auth0Id,
            email,
            emailVerified,
            name,
            picture,
            permissions: loginPermissions,
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            role: userData.role || (loginPermRoles.length > 0 ? loginPermRoles[0] : undefined),
          })

          // Sync to Zustand authStore so Sidebar and useAuth() have permissions
          // We need to call /auth/me to get the full user profile for the store
          try {
            const meResponse = await api.get('/auth/me')
            const meData = meResponse.data
            useAuthStore.setState({
              user: {
                id: meData.id,
                email: meData.email,
                first_name: meData.first_name,
                last_name: meData.last_name,
                is_active: meData.is_active,
                is_verified: meData.is_verified,
                created_at: meData.created_at,
                role: meData.role || (loginPermRoles.length > 0 ? loginPermRoles[0] : undefined),
                permissions: loginPermissions,
                is_super_admin: meData.is_super_admin,
                subscription_active: meData.subscription_active,
                subscription_tier: meData.subscription_tier,
                has_app_access: meData.has_app_access,
                is_client_admin: meData.is_client_admin,
                is_superuser_for_app: meData.is_superuser_for_app,
              },
              isAuthenticated: true,
              isLoading: false,
            })
          } catch (meErr) {
          // Error handled silently
          }

          setIsLoading(false)
          toast({ title: 'Login successful!' })
          navigate('/')
        } catch (loginErr: unknown) {
          // Handle user not found or inactive
          const axiosError = loginErr as { response?: { status: number; data?: { detail?: Auth0LoginError } } }

          if (axiosError.response?.status === 404) {
            // User not found in database
            const errorDetail = axiosError.response.data?.detail as Auth0LoginError

            // Clear tokens since user doesn't have access
            localStorage.removeItem('auth0_access_token')
            localStorage.removeItem('auth0_id_token')
            delete api.defaults.headers.common['Authorization']

            // Store error info for the account-not-found page
            sessionStorage.setItem('auth0_login_error', JSON.stringify({
              email,
              errorCode: errorDetail?.error_code || 'USER_NOT_FOUND',
              message: errorDetail?.message || 'No account found for this email.',
              supportEmail: errorDetail?.support_email,
            }))

            navigate('/account-not-found')
            return
          }

          if (axiosError.response?.status === 403) {
            // User is inactive
            const errorDetail = axiosError.response.data?.detail as Auth0LoginError

            // Clear tokens
            localStorage.removeItem('auth0_access_token')
            localStorage.removeItem('auth0_id_token')
            delete api.defaults.headers.common['Authorization']

            sessionStorage.setItem('auth0_login_error', JSON.stringify({
              email,
              errorCode: errorDetail?.error_code || 'USER_INACTIVE',
              message: errorDetail?.message || 'Your account has been deactivated.',
              supportEmail: errorDetail?.support_email,
            }))

            navigate('/account-not-found')
            return
          }

          // Other errors
          throw loginErr
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Token exchange failed'
        setError(new Error(message))
        toast({ title: 'Authentication failed', variant: 'destructive' })
        callbackProcessingRef.current = false
        navigate('/login')
      }
    }

    if (config) {
      handleCallback()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isE2EBypass is constant (env var)
  }, [location, config, navigate, refreshUser])

  // Check for existing token on mount
  useEffect(() => {
    if (isE2EBypass) return
    const token = localStorage.getItem('auth0_access_token')
    if (token && config) {
      refreshUser()
    } else if (!config) {
      // Config not loaded yet or Auth0 not configured
      setIsLoading(false)
    } else {
      setIsLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isE2EBypass is constant (env var)
  }, [config, refreshUser])

  // Redirect to Auth0 login
  const loginWithRedirect = useCallback(async () => {
    let authConfig = config

    // If config is missing, try to reload it (might have failed due to expired token)
    if (!authConfig) {
      try {
        // Clear any expired tokens first so they don't interfere with config fetch
        localStorage.removeItem('auth0_access_token')
        localStorage.removeItem('auth0_id_token')
        delete api.defaults.headers.common['Authorization']

        const response = await api.get('/auth/config')
        authConfig = response.data
        setConfig(authConfig)
      } catch (err) {
        toast({ title: 'Unable to connect to authentication service. Please try again.', variant: 'destructive' })
        return
      }
    }

    if (!authConfig || !authConfig.domain || !authConfig.clientId) {
      toast({ title: 'Authentication service is not configured. Please contact your administrator.', variant: 'destructive' })
      return
    }

    // Generate PKCE values
    const state = generateRandomString(32)
    const codeVerifier = generateRandomString(64)

    // Store for callback verification
    sessionStorage.setItem('auth0_state', state)
    sessionStorage.setItem('auth0_code_verifier', codeVerifier)

    // Generate code challenge
    generateCodeChallenge(codeVerifier).then(codeChallenge => {
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: authConfig!.clientId,
        redirect_uri: `${window.location.origin}/callback`,
        scope: 'openid profile email',
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      })

      if (authConfig!.audience) {
        params.append('audience', authConfig!.audience)
      }

      window.location.href = `https://${authConfig!.domain}/authorize?${params.toString()}`
    })
  }, [config])

  // Logout - matches Consumer Duty pattern
  const logout = useCallback(async (returnTo?: string) => {
    const returnUrl = returnTo || window.location.origin

    // Clear local storage first
    localStorage.removeItem('auth0_access_token')
    localStorage.removeItem('auth0_id_token')
    localStorage.removeItem('auth0_user_email')
    delete api.defaults.headers.common['Authorization']
    setUser(null)

    // Redirect to Auth0 logout to clear SSO session
    if (config) {
      const params = new URLSearchParams({
        client_id: config.clientId,
        returnTo: returnUrl,
      })
      window.location.href = `https://${config.domain}/v2/logout?${params}`
    } else {
      // No config available - just navigate to login
      toast({ title: 'Logged out successfully' })
      navigate('/login')
    }
  }, [navigate, config])

  // Get access token
  const getAccessToken = useCallback((): string | null => {
    return localStorage.getItem('auth0_access_token')
  }, [])

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    loginWithRedirect,
    logout,
    getAccessToken,
    refreshUser,
  }), [user, isLoading, error, loginWithRedirect, logout, getAccessToken, refreshUser])

  // Auth bypass for E2E tests — return bypass context after all hooks are declared
  if (isE2EBypass) {
    const bypassValue: Auth0ContextType = {
      user: null,
      isAuthenticated: true,
      isLoading: false,
      error: null,
      loginWithRedirect: () => {},
      logout: () => {},
      getAccessToken: () => 'e2e-bypass-token',
      refreshUser: async () => {},
    }
    return (
      <Auth0Context.Provider value={bypassValue}>
        {children}
      </Auth0Context.Provider>
    )
  }

  return (
    <Auth0Context.Provider value={value}>
      {children}
    </Auth0Context.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth0() {
  const context = useContext(Auth0Context)
  if (context === undefined) {
    throw new Error('useAuth0 must be used within an Auth0Provider')
  }
  return context
}
